import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** Tiempo máximo durante el que se persigue el destino mientras el layout se asienta. */
export const SCROLL_TO_HASH_TIMEOUT_MS = 2500;

/** Interacciones del usuario que cancelan el ajuste: no se pelea con quien ya está desplazándose. */
const USER_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;

function targetFor(hash: string): HTMLElement | null {
  const id = decodeURIComponent(hash.replace(/^#/, ''));
  return id ? document.getElementById(id) : null;
}

/**
 * Lleva la vista al elemento de `hash` y la mantiene ahí mientras la página
 * termina de montarse. Devuelve la función de limpieza.
 *
 * - Si el elemento aún no existe, un `MutationObserver` espera a que aparezca.
 * - Si el layout se desplaza (secciones que llegan de la API, imágenes,
 *   fuentes), un `ResizeObserver` sobre `body` vuelve a ajustar.
 * - Todo se detiene a los `SCROLL_TO_HASH_TIMEOUT_MS` o en cuanto el usuario
 *   usa la rueda, toca, pulsa una tecla o hace clic.
 *
 * El margen del AppBar fijo lo aporta `scroll-margin-top` (ver `globals.css`),
 * que `scrollIntoView` respeta.
 */
function followHash(hash: string, behavior: ScrollBehavior): () => void {
  let stopped = false;

  const scroll = () => {
    if (stopped) return;
    targetFor(hash)?.scrollIntoView({ behavior, block: 'start' });
  };

  const mo = new MutationObserver(scroll);
  mo.observe(document.body, { childList: true, subtree: true });

  const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scroll);
  ro?.observe(document.body);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    mo.disconnect();
    ro?.disconnect();
    window.clearTimeout(timer);
    for (const ev of USER_EVENTS) window.removeEventListener(ev, stop);
  };

  const timer = window.setTimeout(stop, SCROLL_TO_HASH_TIMEOUT_MS);
  for (const ev of USER_EVENTS) window.addEventListener(ev, stop, { passive: true });

  scroll();
  return stop;
}

/**
 * Scroll al `#hash` de la URL en una página que React renderiza después de que
 * el navegador procese el fragmento (el scroll nativo llega cuando el ancla aún
 * no existe o antes de que el layout se asiente).
 *
 * - Al montar: salto instantáneo (`'instant'`; `'auto'` heredaría el
 *   `scroll-behavior: smooth` de `html` y se animaría desde arriba).
 * - En cambios de hash posteriores (`hashchange` o navegación del router):
 *   scroll suave, como el comportamiento nativo de un enlace `#…`.
 */
export function useScrollToHash() {
  const { hash } = useLocation();
  const stopRef = useRef<(() => void) | null>(null);
  /** Último hash atendido: si no cambia (montaje, StrictMode), el salto es instantáneo. */
  const lastHashRef = useRef<string | null>(null);

  useEffect(() => {
    const start = (h: string, behavior: ScrollBehavior) => {
      stopRef.current?.();
      stopRef.current = h ? followHash(h, behavior) : null;
    };

    const changed = lastHashRef.current !== null && lastHashRef.current !== hash;
    lastHashRef.current = hash;
    start(hash, changed ? 'smooth' : 'instant');

    const onHashChange = () => start(window.location.hash, 'smooth');
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      stopRef.current?.();
      stopRef.current = null;
    };
  }, [hash]);
}
